import { captureException } from "@sentry/nextjs";

import {
  EventDataBody,
  OfferBody,
  UserBodyWithConsents,
  UserResponse,
} from "@ud/api";
import useUserCRM from "hooks/useUserCRM";
import { EOrderTypeId } from "utils/payment/types";
import { IUser } from "utils/user";
import useGlobalState from "./useGlobalState";
import { ALL_PREMIUM_ORDER_TYPES } from "config/orderTypes";

interface IUpdateUserAfterPayment {
  user: IUser;
  orderType?: number | string | Array<string>;
}

interface ICreateEventWatchdogCreated {
  userEmail: string;
  userId?: number;
  eventData?: EventDataBody;
}

interface ICreateEventOffer {
  action: EAction;
}

interface ICreateUserOffer {
  data: OfferBody;
}

interface IUpdateUserOffer {
  data: OfferBody;
  offerId: string;
}

interface IUserEventCRM {
  updateUserAfterPayment: (params: IUpdateUserAfterPayment) => void;
  createEventWatchdogCreated: (params: ICreateEventWatchdogCreated) => void;
  createEventOffer: (params: ICreateEventOffer) => void;
  createEventOfferDraft: () => void;
  createUserOffer: (params: ICreateUserOffer) => void;
  updateUserOffer: (params: IUpdateUserOffer) => void;
  createOrUpdateUser: (
    email: string,
    userBodyWithConsents: UserBodyWithConsents,
  ) => Promise<UserResponse["id"] | undefined>;
}

export type TEventDataWatchdogCreated = {
  frequency: string;
  city: string;
  dispositions: Array<string>;
  furnishing: Array<string>;
  noCommission: boolean;
};

export enum EAction {
  CREATE = "CREATE",
  PUBLISH = "PUBLISH",
  DEACTIVATE = "DEACTIVATE",
}

const useHelperCRM = (): IUserEventCRM => {
  const {
    getUserByEmail,
    createUser,
    updateUser,
    createEvent,
    createOffer,
    updateOffer,
  } = useUserCRM();
  const { user } = useGlobalState();

  const updateUserAfterPayment = async (
    params: IUpdateUserAfterPayment,
  ): Promise<void> => {
    const userFromCRM = await getUserByEmail(params.user.email);
    if (userFromCRM) {
      await updateUser(userFromCRM.id, {
        premiumExpirationDate: params.user.premiumTill || undefined,
        selfCheckExpirationDate: params.user.selfCheckTill || undefined,
      });
      if (ALL_PREMIUM_ORDER_TYPES.includes(Number(params.orderType))) {
        createEvent({
          userId: userFromCRM.id,
          eventName: "premium_paid",
        });
      }
      if (Number(params.orderType) === EOrderTypeId.IMMENDIATE_PHONE) {
        createEvent({
          userId: userFromCRM.id,
          eventName: "phone_number_viewer_paid",
        });
      }
    } else {
      const userRes = await createUser({
        email: params.user.email,
        userId: params.user.id,
        premiumExpirationDate: params.user.premiumTill || undefined,
        selfCheckExpirationDate: params.user.selfCheckTill || undefined,
        consents: [],
        offers: [],
      });
      if (userRes) {
        if (ALL_PREMIUM_ORDER_TYPES.includes(Number(params.orderType))) {
          createEvent({
            userId: userRes.id,
            eventName: "premium_paid",
          });
        }
        if (Number(params.orderType) === EOrderTypeId.IMMENDIATE_PHONE) {
          createEvent({
            userId: userRes.id,
            eventName: "phone_number_viewer_paid",
          });
        }
      }
    }
  };

  const createEventWatchdogCreated = async (
    params: ICreateEventWatchdogCreated,
  ): Promise<void> => {
    const userFromCRM = await getUserByEmail(params.userEmail);
    if (userFromCRM) {
      createEvent({
        userId: userFromCRM.id,
        eventName: "watchdog_created",
        eventData: params.eventData,
      });
    } else {
      const userRes = await createUser({
        email: params.userEmail,
        userId: params.userId ?? user?.id,
        consents: [],
        offers: [],
      });
      if (userRes) {
        createEvent({
          userId: userRes.id,
          eventName: "watchdog_created",
          eventData: params.eventData,
        });
      }
    }
  };

  const createEventOffer = async ({
    action,
  }: ICreateEventOffer): Promise<void> => {
    if (!user) {
      return;
    }

    const userFromCRM = await getUserByEmail(user.email);
    if (userFromCRM) {
      createEvent({
        userId: userFromCRM.id,
        eventName: "offer",
        eventData: {
          date: new Date().toISOString(),
          action,
        },
      });
    }
  };

  const createEventOfferDraft = async (): Promise<void> => {
    if (!user) {
      return;
    }

    const userFromCRM = await getUserByEmail(user?.email);

    if (userFromCRM) {
      createEvent({
        userId: userFromCRM.id,
        eventName: "offer_draft",
      });
    }
  };

  const createUserOffer = async ({ data }: ICreateUserOffer) => {
    if (!user) {
      return;
    }
    const userFromCRM = await getUserByEmail(user?.email);
    if (userFromCRM) {
      await createOffer({ data, userId: userFromCRM.id });
    }
  };

  const updateUserOffer = async ({ data, offerId }: IUpdateUserOffer) => {
    if (!user) {
      return;
    }
    const userFromCRM = await getUserByEmail(user?.email);
    if (userFromCRM) {
      await updateOffer({ data, offerId, userId: userFromCRM.id });
    }
  };

  const createOrUpdateUser = async (
    email: string,
    userBodyWithConsents: UserBodyWithConsents,
  ): Promise<UserResponse["id"] | undefined> => {
    try {
      const userFromCRM = await getUserByEmail(email);
      if (userFromCRM?.id) {
        const reqUpdateUser = await updateUser(
          userFromCRM.id,
          userBodyWithConsents,
        );

        return reqUpdateUser?.id;
      }

      const reqCreateUser = await createUser({
        ...userBodyWithConsents,
        userId: userBodyWithConsents.userId ?? user?.id,
      });

      return reqCreateUser?.id;
    } catch (e) {
      captureException(e);

      return undefined;
    }
  };

  return {
    updateUserAfterPayment,
    createEventWatchdogCreated,
    createEventOffer,
    createEventOfferDraft,
    createUserOffer,
    updateUserOffer,
    createOrUpdateUser,
  };
};

export default useHelperCRM;
